import { AxiosError, AxiosInstance } from 'axios';
import { Banks } from 'service/dto/bank.dto';
import { Bonds } from 'service/dto/bond.dto';
import {
  CustomerWillInfo,
  Payment,
  Payments,
  WillHistoryDto
} from 'service/dto/customer.dto';
import { Benefits } from 'service/dto/employee-benefit.dto';
import { PensionAccounts } from 'service/dto/pension-account.dto';
import { PhysicalAssets } from 'service/dto/physical-asset.dto';
import { ArchivedCustomerDto, CustomerListType } from 'service/dto/staff.dto';
import { StockShares } from 'service/dto/stock-share.dto';
import { UserInfoDto } from 'service/dto/user.dto';
import { ApiPaginationOptions } from 'types/general.type';
import { errorHandler } from 'utils';

class ManageCustomerService {
  private baseService: AxiosInstance;

  constructor(baseService: AxiosInstance) {
    this.baseService = baseService;
  }

  getCustomers = async (
    whereOptions: any,
    paginationOptions: ApiPaginationOptions
  ): Promise<CustomerListType> => {
    const response = await this.baseService.get('/customer/list', {
      params: {
        whereOptions,
        paginationOptions
      }
    });
    return response.data;
  };

  getCustomer = async (customerId: any): Promise<UserInfoDto> => {
    try {
      const response = await this.baseService.get(
        `/customer/details/${customerId}`
      );
      return response.data;
    } catch (error) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  getArchivedCustomer = async (
    customerId: any
  ): Promise<ArchivedCustomerDto> => {
    try {
      const response = await this.baseService.get(
        `/customer/archived/${customerId}`
      );
      return response.data;
    } catch (error) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  deactivateCustomer = async (payload: any) => {
    try {
      const response = await this.baseService.put(
        `/customer/deactivate/${payload.customerId}`,
        { reason: payload.note }
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  activateCustomer = async (customerId: any) => {
    try {
      const response = await this.baseService.put(
        `/customer/reactivate/${customerId}`
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  archiveCustomer = async (payload: any, customerId: any) => {
    try {
      const response = await this.baseService.put(
        `/customer/archive/${customerId}`,
        payload
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  getCustomerStats = async (customerId: any) => {
    try {
      const response = await this.baseService.get(
        `/customer/stats/${customerId}`
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  getActivities = async (customerId: any) => {
    try {
      const response = await this.baseService.get(
        `/customer/activities/${customerId}`
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  getCustomerBanks = async (customerId: any): Promise<Banks> => {
    try {
      const response = await this.baseService.get(
        `/customer/banks/${customerId}`
      );
      return response.data;
    } catch (error) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  getCustomerPensionAccounts = async (
    customerId: any
  ): Promise<PensionAccounts> => {
    try {
      const response = await this.baseService.get(
        `/customer/pensions/${customerId}`
      );
      return response.data;
    } catch (error) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  getCustomerBonds = async (customerId: any): Promise<Bonds> => {
    try {
      const response = await this.baseService.get(
        `/customer/bonds/${customerId}`
      );
      return response.data;
    } catch (error) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  getCustomerStocks = async (customerId: any): Promise<StockShares> => {
    try {
      const response = await this.baseService.get(
        `/customer/stocks/${customerId}`
      );
      return response.data;
    } catch (error) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  getCustomerPhysicals = async (customerId: any): Promise<PhysicalAssets> => {
    try {
      const response = await this.baseService.get(
        `/customer/physicals/${customerId}`
      );
      return response.data;
    } catch (error) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  getCustomerBenefits = async (customerId: any): Promise<Benefits> => {
    try {
      const response = await this.baseService.get(
        `/customer/benefits/${customerId}`
      );
      return response.data;
    } catch (error) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  approveRejectWill = async (payload: any) => {
    try {
      const response = await this.baseService.put(
        `/customer/approve-will/${payload.customerId}/${payload.staffId}`,
        { comment: payload.note, isApproved: payload.isApproved }
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  assignStaff = async (staffId: any, customerId: any) => {
    try {
      const response = await this.baseService.put(
        `/customer/assign-staff/${customerId}/${staffId}`
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  getCustomerPayments = async (
    whereOptions: any,
    paginationOptions: ApiPaginationOptions
  ): Promise<Payments> => {
    const response = await this.baseService.get('/customer/payments', {
      params: {
        whereOptions,
        paginationOptions
      }
    });
    return response.data;
  };

  paymentDetails = async (paymentId: any): Promise<Payment> => {
    try {
      const response = await this.baseService.get(
        `/customer/payments/${paymentId}`
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  getCustomerInfoDetails = async (
    customerId: any
  ): Promise<CustomerWillInfo> => {
    try {
      const response = await this.baseService.get(
        `/customer/will-stat/${customerId}`
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  uploadSignedWill = async (payload: any): Promise<CustomerWillInfo> => {
    try {
      const response = await this.baseService.post(
        `/customer/upload-signed-will`,
        payload,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  getCustomerWills = async (
    whereOptions: any,
    paginationOptions: ApiPaginationOptions
  ): Promise<WillHistoryDto> => {
    const response = await this.baseService.get('/customer/will-history', {
      params: {
        whereOptions,
        paginationOptions
      }
    });
    return response.data;
  };

  allowWillModification = async (
    customerId: any
  ): Promise<CustomerWillInfo> => {
    try {
      const response = await this.baseService.put(
        `/customer/allow-will-modification/${customerId}`
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };

  viewGeneratedPDF = async (userId: number, willId: number) => {
    try {
      const response = await this.baseService.get(
        `/customer/view-will-pdf/${userId}/${willId}`,
        {
          timeout: 40000
        }
      );
      return response?.data;
    } catch (error: any) {
      const err = error as AxiosError<Error>;
      return errorHandler(err);
    }
  };
}

export default ManageCustomerService;
